import { ProductTag } from '@woolworthsnz/trader-api';
import { isPlatformServer, isPlatformBrowser } from '@angular/common';
import * as i0 from '@angular/core';
import { isDevMode, PLATFORM_ID, Injectable, Inject } from '@angular/core';
import { Subject } from 'rxjs';

/* eslint-disable @typescript-eslint/naming-convention */
const productDetailsConstants = {
    tealium_event: 'product_view',
    interaction_source: 'page',
};
const productViewConstants = {
    tealium_event: 'view_item',
};
const productGridConstants = {
    tealium_event: 'product_list_view',
    interaction_source: 'page',
};
const productClearConstants = {
    tealium_event: 'cart_clear',
    interaction_source: 'page',
    cart_step: '',
    cart_step_label: '',
    cart_payment_type: '',
};
const productSelectConstants = {
    tealium_event: 'select_item',
};
const defaultSalesPrice = '0.00';
const stockAvailabilityConstants = {
    inStock: 'In Stock',
    outOfStock: 'Out of Stock',
};
class TealiumProductList {
    constructor() {
        this.product_category_1 = [];
        this.product_category_2 = [];
        this.product_category_3 = [];
        this.product_sku = [];
        this.product_image_url = [];
        this.product_name = [];
        this.product_price = [];
        this.product_sale_price = [];
        this.product_dietary = [];
        this.product_stock = [];
        this.product_brand = [];
        this.product_quantity = [];
        this.product_promo_category = [];
        this.product_promo_value = [];
        this.search_value = [];
        this.search_results_returned = [];
    }
}

const purchaseConstants = {
    tealium_event: 'purchase',
};

function mapEmptyValues(values) {
    // convert null & undefined values to empty strings for analytics purposes
    return values.map((value) => {
        if (value === null || value === undefined) {
            return '';
        }
        return value;
    });
}
function sanitize(data) {
    const result = {};
    for (const key in data) {
        if (Array.isArray(data[key])) {
            result[key] = mapEmptyValues(data[key]);
        }
        else {
            result[key] = data[key];
        }
    }
    return result;
}
function safeStringSplit(input, delimiter) {
    if (typeof input !== 'string') {
        return [];
    }
    return input.split(delimiter);
}

/**
 * Returns stringified version of whether a product is in stock or not
 */
const isProductAvailableString = (productIsAvailable) => productIsAvailable ? stockAvailabilityConstants.inStock : stockAvailabilityConstants.outOfStock;
const isProductAvailable$1 = (product) => !!product?.price?.salePrice && !!product?.quantity?.max;
const getPromoValueFromCatagory = (product, tagtype) => {
    switch (tagtype) {
        case ProductTag.TagTypeEnum.IsMultiBuy:
            return product.productTag?.multiBuy?.link || '';
        case ProductTag.TagTypeEnum.IsTargetedOffer:
        case ProductTag.TagTypeEnum.IsClubPrice:
            return product.productTag?.targetedOffer?.offerValue?.toString() || '';
        case ProductTag.TagTypeEnum.HasBonusPoints:
            return product.productTag?.bonusPoints?.quantity?.toString() || '';
        default:
            return '';
    }
};
const getAnalyticsData = (products = [], department, aisle, shelf) => products.reduce((productList, product, currentIndex) => {
    productList.product_category_1[currentIndex] = department || '';
    productList.product_category_2[currentIndex] = aisle || '';
    productList.product_category_3[currentIndex] = shelf || '';
    productList.product_brand[currentIndex] = product.brand || '';
    productList.product_sku[currentIndex] = product.sku || '';
    productList.product_image_url[currentIndex] = product.images?.big || '';
    productList.product_name[currentIndex] = product.name || '';
    productList.product_price[currentIndex] = product.price?.originalPrice?.toString() || '';
    productList.product_sale_price[currentIndex] = product.price?.salePrice
        ? product.price.salePrice.toFixed(2)
        : '';
    productList.product_stock[currentIndex] = isProductAvailableString(isProductAvailable$1(product));
    productList.product_dietary[currentIndex] = '';
    productList.product_quantity[currentIndex] = product.quantity?.value?.toString() || '';
    productList.search_value[currentIndex] = product.searchValue || '';
    productList.search_results_returned[currentIndex] = product.searchResultsCount || '';
    productList.product_promo_category[currentIndex] = product.productTag?.tagType || '';
    productList.product_promo_value[currentIndex] = getPromoValueFromCatagory(product, product.productTag?.tagType);
    return productList;
}, new TealiumProductList());
const currencyToNumber = (value) => Number(value.substring(1));
const mapToTealiumProduct = (product, position) => ({
    item_id: product.sku ?? '',
    item_name: product.name ?? '',
    discount: product.price?.savePrice ?? 0,
    index: position?.toString(),
    item_brand: product.brand ?? '',
    // item_category: product?.department?.name,
    // item_category2: product?.aisle?.name,
    // item_category3: product?.shelf?.name,
    // item_variant: '',
    price: product.price?.salePrice ?? 0,
    quantity: product.quantity?.value ?? product.quantity?.quantityInOrder ?? 0,
    each_kg: product.unit?.toString() ?? '',
});
const getBoostOfferProducts = (products) => products.filter((product) => product.productTag?.tagType === ProductTag.TagTypeEnum.IsBoostOffer);
const getTotalBoostValue = (products) => products.reduce((acc, product) => acc + (product.productTag?.boostOffer?.rewardValue.value ?? 0), 0);
const getProductsPriceTotal = (products) => products.reduce((acc, product) => acc + currencyToNumber(product.price?.total || '0'), 0);

const mapToTealiumEcommerce = (order, products) => {
    const boostProducts = getBoostOfferProducts(products);
    return {
        transaction_id: order.id?.toString() || '',
        value: order.totals?.amountToPay ?? 0,
        // tax: 0,
        shipping: order.totals?.deliveryFee ?? 0,
        currency: 'NZD',
        // coupon: '',
        number_of_boosts: boostProducts.length,
        boosts_value: getTotalBoostValue(boostProducts),
        items: products.map((product, index) => mapToTealiumProduct(product, index)),
    };
};

var ProductType;
(function (ProductType) {
    ProductType["Organic"] = "Organic";
    ProductType["CPP"] = "CPP";
    ProductType["ProductInGrid"] = "Product in Grid";
    ProductType["CartologyRemarketing"] = "Cartology_Remarketing";
})(ProductType || (ProductType = {}));

const COLLECT_TIMEOUT_MS = 350;
var AnalyticsCollectionType;
(function (AnalyticsCollectionType) {
    AnalyticsCollectionType[AnalyticsCollectionType["RecipeStampImpression"] = 0] = "RecipeStampImpression";
    AnalyticsCollectionType[AnalyticsCollectionType["ProductImpression"] = 1] = "ProductImpression";
    AnalyticsCollectionType[AnalyticsCollectionType["ViewItemList"] = 2] = "ViewItemList";
    AnalyticsCollectionType[AnalyticsCollectionType["ViewPromotion"] = 3] = "ViewPromotion";
})(AnalyticsCollectionType || (AnalyticsCollectionType = {}));
class AnalyticsCollectionBuffer {
    constructor(link) {
        this.link = link;
        this.events = [];
        this.timer = 0;
    }
    push(data) {
        if (window) {
            // do nothing in non-browser environments
            this.events.push(sanitize(data));
            window.clearTimeout(this.timer);
            this.timer = window.setTimeout(this.flush.bind(this), COLLECT_TIMEOUT_MS);
        }
    }
    pushItemList(data) {
        if (window) {
            // do nothing in non-browser environments
            this.events.push(sanitize(data));
            window.clearTimeout(this.timer);
            this.timer = window.setTimeout(this.flushItemList.bind(this), COLLECT_TIMEOUT_MS);
        }
    }
    flush() {
        this.link(this.events.reduce((accum, curr) => {
            const result = { ...accum, ...curr }; // non-array values on the same key are overwritten
            for (const key in curr) {
                if (Array.isArray(curr[key]) && Array.isArray(accum[key])) {
                    result[key] = accum[key].concat(curr[key]);
                }
            }
            return result;
        }, {}));
        this.events = [];
    }
    flushItemList() {
        const items = this.events.filter((event) => event['0']).map((event) => event['0']);
        if (items.length > 0 && this.events[0].event && this.events[0].event === 'view_item_list') {
            const itemListResult = {
                event: this.events[0].event,
                tealium_event: this.events[0].tealium_event,
                ecommerce: {
                    item_list_id: this.events[0].item_list_id,
                    item_list_name: this.events[0].item_list_name,
                    items: items,
                },
            };
            this.link(itemListResult);
        }
        this.events = [];
    }
}

const isProductAvailable = (product) => !!product?.price?.salePrice && !!product?.quantity?.max;
class TealiumUtagService {
    static { this.collectionBuffers = {}; }
    static { this.scriptSrc = ''; }
    constructor(platform) {
        this.platform = platform;
        // data that will be passed with every tracking event
        this.viewItemBatches = new Map();
        this.viewItemBatchSubject = new Subject();
        this.noCarouselName = '**NONE**';
        this.sharedAnalyticsData = {};
        this.enableDataLayerLogging = false;
        this.mapItemToDatalayerEvent = (products) => {
            let ar = [];
            products.map((p, i) => {
                if (p.type === 'Product') {
                    const availabilityStatus = isProductAvailable(p) ? p.availabilityStatus ?? 'In Stock' : 'Unavailable';
                    ar = [
                        ...ar,
                        {
                            id: p.sku,
                            name: p.name,
                            position: p.position ?? i + 1,
                            promotionProductTags: p.productTag?.tagType ?? '',
                            discout: p.price?.discount,
                            itemBrand: p.brand ?? '',
                            itemCategory: p.departments?.[0]?.name ?? '',
                            itemCategory2: p.departments?.[1]?.name ?? '',
                            itemCategory3: p.departments?.[2]?.name ?? '',
                            itemListId: '',
                            itemListName: p.brand === 'online sample' ? 'Online Sample' : '',
                            itemVariant: p.variety ?? '',
                            price: p.price ? p.price.salePrice : null,
                            hasShopperNotes: p.hasShopperNotes,
                            quantity: p.quantity?.value,
                            eachKg: p.unit ?? '',
                            availabilityStatus,
                        },
                    ];
                }
            });
            return ar;
        };
        if (isPlatformServer(this.platform)) {
            return;
        }
        // Set "noview" flag (no first page automatic view event) to true for Single Page Apps (SPAs)
        window.utag_cfg_ovrd = { noview: true };
        window.utag_data = {};
    }
    // The below code should be kept as is or updated from:
    // https://docs.tealium.com/platforms/angular/install/
    /* eslint-disable */
    // Generic script loader with callback
    getScript(src, callback) {
        const d = document;
        const fn = () => { };
        const o = { callback: callback || fn };
        let s;
        let t;
        if (typeof src === 'undefined') {
            return;
        }
        s = d.createElement('script');
        s.language = 'javascript';
        s.type = 'text/javascript';
        s.async = 1;
        s.charset = 'utf-8';
        s.src = src;
        if (typeof o.callback === 'function') {
            if (d.addEventListener) {
                d.addEventListener('load', () => {
                    o.callback();
                }, false);
                s.addEventListener('load', () => {
                    o.callback();
                }, false);
            }
            else {
                // old IE support
                s.onreadystatechange = function () {
                    if (this.readyState === 'complete' || this.readyState === 'loaded') {
                        this.onreadystatechange = null;
                        o.callback();
                    }
                };
            }
        }
        t = d.getElementsByTagName('script')[0];
        t.parentNode.insertBefore(s, t);
    }
    // Config settings used to build the path to the utag.js file
    setConfig(config) {
        if (config.account !== undefined && config.profile !== undefined && config.environment !== undefined) {
            TealiumUtagService.scriptSrc = `https://tags.tiqcdn.com/utag/${config.account}/${config.profile}/${config.environment}/utag.js`;
        }
    }
    // Data layer is optional set of key/value pairs
    track(tealiumEvent, data) {
        if (TealiumUtagService.scriptSrc === '') {
            if (window?.BOOTSTRAP_DATA?.Tealium) {
                this.setConfig(window.BOOTSTRAP_DATA.Tealium);
            }
            else {
                return;
            }
        }
        const trackData = { ...data, ...this.sharedAnalyticsData };
        if (window.utag === undefined) {
            this.getScript(TealiumUtagService.scriptSrc, () => {
                window.utag.track(tealiumEvent, trackData);
            });
        }
        else {
            window.utag.track(tealiumEvent, trackData);
        }
    }
    view(data, copyToLegacyDataLayer = false) {
        this.track('view', data);
        if (copyToLegacyDataLayer) {
            this.logToLegacyDataLayer(data);
        }
    }
    link(data, copyToLegacyDataLayer = false) {
        this.track('link', sanitize(data));
        if (copyToLegacyDataLayer) {
            this.logToLegacyDataLayer(data);
        }
    }
    logToLegacyDataLayer(data) {
        if (isPlatformBrowser(this.platform) && window['dataLayer']) {
            window['dataLayer'].push(data);
        }
    }
    // Debounces events of a given type into a single call to 'link'
    collect(type, data) {
        if (isPlatformServer(this.platform)) {
            return;
        }
        if (!(type in TealiumUtagService.collectionBuffers)) {
            TealiumUtagService.collectionBuffers[type] = new AnalyticsCollectionBuffer(this.link.bind(this));
        }
        TealiumUtagService.collectionBuffers[type].push(data);
    }
    collectViewItems(type, data) {
        if (isPlatformServer(this.platform)) {
            return;
        }
        if (!(type in TealiumUtagService.collectionBuffers)) {
            TealiumUtagService.collectionBuffers[type] = new AnalyticsCollectionBuffer(this.link.bind(this));
        }
        TealiumUtagService.collectionBuffers[type].pushItemList(data);
    }
    setSharedData(data = {}) {
        this.sharedAnalyticsData = { ...this.sharedAnalyticsData, ...data };
    }
    trackViewItemList(products) {
        if (!Array.isArray(products)) {
            return;
        }
        const productDatalayerEvent = this.mapItemToDatalayerEvent(products);
        if (isDevMode() && console.table && this.enableDataLayerLogging) {
            console.info('[DatalayerService]: trackViewItemList called');
            console.table(productDatalayerEvent);
        }
        this.link({
            event: 'viewItemList',
            ecommerce: {
                currencyCode: 'NZD',
                impressions: [...productDatalayerEvent],
            },
        }, true);
    }
    /**
     * This will add a viewItemList to an existing batch of viewItemList.
     * It also triggers the observer event
     * @param viewItems
     * @param carouselName
     */
    addViewItemsToBatch(viewItems, carouselName) {
        const key = carouselName || this.noCarouselName;
        if (this.viewItemBatches.has(key)) {
            const batchedProducts = this.viewItemBatches.get(key) ?? [];
            this.viewItemBatches.set(key, [...batchedProducts, ...viewItems]);
        }
        else {
            this.viewItemBatches.set(key, [...viewItems]);
        }
        // Trigger batch processing
        this.viewItemBatchSubject.next();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.10", ngImport: i0, type: TealiumUtagService, deps: [{ token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.10", ngImport: i0, type: TealiumUtagService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.10", ngImport: i0, type: TealiumUtagService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AnalyticsCollectionBuffer, AnalyticsCollectionType, COLLECT_TIMEOUT_MS, ProductType, TealiumProductList, TealiumUtagService, defaultSalesPrice, getAnalyticsData, getBoostOfferProducts, getProductsPriceTotal, getTotalBoostValue, isProductAvailableString, mapToTealiumEcommerce, mapToTealiumProduct, productClearConstants, productDetailsConstants, productGridConstants, productSelectConstants, productViewConstants, purchaseConstants, safeStringSplit, sanitize, stockAvailabilityConstants };
